import type { ProjectDetail, ProjectSummary } from "../features/projects/types";
import {
  acknowledgeSchedulingJob,
  acknowledgeRepairAcceptance,
  acceptRepairCandidate,
  applyPrecheckedSchedule,
  candidateTimetable,
  copyDocument,
  createPartialRepairRound,
  createRepairRound,
  createRepairSession,
  downloadPlanningImportTemplate,
  executeDocumentCommand,
  exportDocument,
  getRepairSession,
  invalidateRepairSession,
  getSchedulingJob,
  submitSchedulingJob,
  type CommandResponse,
  type ExportDownload,
  type SchedulingAlternativeHistory,
  type SchedulingCandidate,
  type SchedulingJobStatus,
  type RepairSessionStatus,
  type SchedulingPrecheckResult,
  type SchedulingSearchMode
} from "../api/statelessClient";
import { previewRepairCandidate } from "../api/statelessClient";
import {
  createEmptyProjectDocument,
  normalizeProjectName,
  projectDetail,
  projectSummary,
  renameProjectDocument,
  type ProjectDocumentV3
} from "../storage/projectDocument";
import {
  openProjectStore,
  ProjectRevisionConflictError,
  type ProjectStore
} from "../storage/projectStore";
import {
  openSchedulingPrecheckStore,
  type SchedulingPrecheckRecordV1,
  type SchedulingPrecheckStore
} from "../storage/schedulingPrecheckStore";
import {
  openTimetableVersionStore,
  type TimetableVersionRecord,
  type TimetableVersionStore,
  type VersionApplication
} from "../storage/timetableVersionStore";

export class ProjectRepositoryError extends Error {
  readonly code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = "ProjectRepositoryError";
    this.code = code;
  }
}

export class ProjectRepository {
  constructor(
    private readonly store: ProjectStore,
    private readonly versions: TimetableVersionStore,
    private readonly prechecks: SchedulingPrecheckStore
  ) {}

  async list(query = ""): Promise<{ items: ProjectSummary[] }> {
    const documents = await this.store.list(query);
    return { items: documents.map(projectSummary) };
  }

  async create(name: string): Promise<ProjectSummary> {
    await this.requireUniqueName(name);
    const document = createEmptyProjectDocument(name);
    await this.store.putIfRevision(document, null);
    return projectSummary(document);
  }

  async read(projectId: string): Promise<ProjectDetail> {
    return projectDetail(await this.requireDocument(projectId));
  }

  async readDocument(projectId: string): Promise<ProjectDocumentV3> {
    return this.requireDocument(projectId);
  }

  async rename(
    projectId: string,
    name: string,
    expectedConditionRevision: number
  ): Promise<ProjectSummary> {
    const current = await this.requireDocument(projectId);
    if (current.condition_revision !== expectedConditionRevision) {
      throw new ProjectRevisionConflictError(
        expectedConditionRevision,
        current.condition_revision
      );
    }
    await this.requireUniqueName(name, projectId);
    const updated = renameProjectDocument(current, name);
    await this.store.putIfRevision(updated, current.document_revision);
    return projectSummary(updated);
  }

  async copy(projectId: string, name: string): Promise<ProjectSummary> {
    await this.requireUniqueName(name);
    const source = await this.requireDocument(projectId);
    const copied = await copyDocument(source, name);
    await this.store.putIfRevision(copied, null);
    return projectSummary(copied);
  }

  async delete(projectId: string): Promise<void> {
    await this.requireDocument(projectId);
    await this.prechecks.delete(projectId);
    await this.store.delete(projectId);
  }

  async execute<T>(
    projectId: string,
    commandType: string,
    payload: Record<string, unknown>,
    signal?: AbortSignal
  ): Promise<T> {
    const current = await this.requireDocument(projectId);
    const requestedVersionId =
      typeof payload.__timetable_version_id === "string"
        ? payload.__timetable_version_id
        : null;
    if (commandType.startsWith("timetable.") && requestedVersionId) {
      const head = await this.versions.head(projectId);
      if (head?.version_id !== requestedVersionId) {
        throw new ProjectRepositoryError(
          "HISTORICAL_VERSION_READ_ONLY",
          "历史课表版本仅供查看和导出，不能修改"
        );
      }
    }
    const commandPayload = { ...payload };
    delete commandPayload.__timetable_version_id;
    const response = await executeDocumentCommand<T>(
      current,
      commandType,
      commandPayload,
      signal
    );
    if (response.document.id !== current.id) {
      throw new ProjectRepositoryError(
        "DOCUMENT_INVALID",
        "计算服务返回了不匹配的课表项目"
      );
    }
    if (response.mutated) {
      if (
        response.document.document_revision === current.document_revision &&
        JSON.stringify(response.document) === JSON.stringify(current)
      ) {
        return response.result;
      }
      if (response.document.document_revision !== current.document_revision + 1) {
        throw new ProjectRepositoryError(
          "DOCUMENT_INVALID",
          "计算服务返回了不连续的课表修订号"
        );
      }
      if (commandType.startsWith("timetable.")) {
        await this.versions.updateActiveSnapshot(
          this.store,
          current,
          response.document
        );
      } else {
        await this.store.putIfRevision(response.document, current.document_revision);
      }
      if (
        response.document.condition_revision !== current.condition_revision ||
        response.document.timetable_revision !== current.timetable_revision
      ) {
        await this.prechecks.delete(projectId);
      }
    } else if (response.document.document_revision !== current.document_revision) {
      throw new ProjectRepositoryError(
        "DOCUMENT_INVALID",
        "只读操作意外修改了课表修订号"
      );
    }
    return response.result;
  }

  async submitScheduling<T>(
    projectId: string,
    jobId: string,
    accessToken: string,
    searchMode: SchedulingSearchMode,
    alternativeHistory?: SchedulingAlternativeHistory,
    signal?: AbortSignal
  ): Promise<SchedulingJobStatus<T>> {
    return submitSchedulingJob<T>(
      await this.requireDocument(projectId),
      jobId,
      accessToken,
      { searchMode, alternativeHistory },
      signal
    );
  }

  async candidateTimetable<T>(
    projectId: string,
    candidate: SchedulingCandidate,
    viewSegment: "classes" | "teachers" | "rooms",
    objectId: string | null,
    weekParity: "ODD" | "EVEN",
    signal?: AbortSignal
  ): Promise<T> {
    return candidateTimetable<T>(
      await this.requireDocument(projectId),
      candidate,
      viewSegment,
      objectId,
      weekParity,
      signal
    );
  }

  async schedulingStatus<T>(
    jobId: string,
    accessToken: string,
    signal?: AbortSignal
  ): Promise<SchedulingJobStatus<T>> {
    return getSchedulingJob<T>(jobId, accessToken, signal);
  }

  async precheckRecord(
    projectId: string
  ): Promise<SchedulingPrecheckRecordV1 | null> {
    const current = await this.requireDocument(projectId);
    const record = await this.prechecks.get(projectId);
    if (!record) return null;
    if (
      record.conditionRevision !== current.condition_revision ||
      record.timetableRevision !== current.timetable_revision ||
      record.candidate.project_id !== current.id
    ) {
      await this.prechecks.delete(projectId);
      return null;
    }
    return record;
  }

  async savePrecheckCandidate(
    projectId: string,
    sourceJobId: string,
    result: SchedulingPrecheckResult,
    alternativeHistory: SchedulingAlternativeHistory | null
  ): Promise<SchedulingPrecheckRecordV1> {
    const current = await this.requireDocument(projectId);
    if (
      result.outcome !== "FEASIBLE" ||
      !result.candidate ||
      result.condition_revision !== current.condition_revision ||
      result.timetable_revision !== current.timetable_revision ||
      result.candidate.project_id !== current.id ||
      result.candidate.input_fingerprint !== result.input_fingerprint
    ) {
      throw new ProjectRepositoryError(
        "PRECHECK_RESULT_INVALID",
        "预检候选与当前课表条件不匹配，请重新预检"
      );
    }
    const record: SchedulingPrecheckRecordV1 = {
      schemaVersion: 1,
      projectId,
      sourceJobId,
      conditionRevision: current.condition_revision,
      timetableRevision: current.timetable_revision,
      inputFingerprint: result.input_fingerprint,
      requestContext: {
        relaxCourseAdjacency: false,
        searchMode: "STRICT",
        alternativeHistory
      },
      savedAt: new Date().toISOString(),
      candidate: structuredClone(result.candidate)
    };
    await this.prechecks.put(record);
    return record;
  }

  async clearPrecheck(projectId: string): Promise<void> {
    await this.prechecks.delete(projectId);
  }

  async applyPrecheckedSchedule<T>(
    projectId: string,
    signal?: AbortSignal
  ): Promise<T> {
    const current = await this.requireDocument(projectId);
    const record = await this.precheckRecord(projectId);
    if (!record) {
      throw new ProjectRepositoryError(
        "PRECHECK_RESULT_NOT_FOUND",
        "已保存的预检结果不存在或已失效，请重新预检"
      );
    }
    const response = await applyPrecheckedSchedule<T>(
      current,
      record.candidate,
      record.sourceJobId,
      record.requestContext,
      signal
    );
    const result = await this.applySchedulingResponse(projectId, response);
    await this.prechecks.delete(projectId);
    return result;
  }

  async applySchedulingResponse<T>(
    projectId: string,
    response: CommandResponse<T>
  ): Promise<T> {
    const current = await this.requireDocument(projectId);
    if (response.document.id !== current.id) {
      throw new ProjectRepositoryError(
        "DOCUMENT_INVALID",
        "排课服务返回了不匹配的课表项目"
      );
    }
    if (response.mutated) {
      const application = versionApplication(response.result);
      const isPendingAcknowledgement =
        application !== null &&
        response.document.document_revision === current.document_revision;
      if (
        response.document.document_revision !== current.document_revision + 1 &&
        !isPendingAcknowledgement
      ) {
        throw new ProjectRevisionConflictError(
          response.document.document_revision - 1,
          current.document_revision
        );
      }
      if (!application) {
        await this.store.putIfRevision(response.document, current.document_revision);
      } else {
        await this.versions.applyApplication(
          this.store,
          current,
          response.document,
          application,
          versionProvenance(response.result)
        );
      }
      if (
        response.document.condition_revision !== current.condition_revision ||
        response.document.timetable_revision !== current.timetable_revision
      ) {
        await this.prechecks.delete(projectId);
      }
    } else if (response.document.document_revision > current.document_revision) {
      throw new ProjectRepositoryError(
        "DOCUMENT_INVALID",
        "未生成课表的任务意外修改了项目修订号"
      );
    }
    return response.result;
  }

  async acknowledgeScheduling(
    jobId: string,
    accessToken: string,
    signal?: AbortSignal
  ): Promise<void> {
    await acknowledgeSchedulingJob(jobId, accessToken, signal);
  }

  async createRepairSession(
    sourceStrictJobId: string,
    sessionId: string,
    roundId: string,
    jobId: string,
    accessToken: string,
    signal?: AbortSignal
  ): Promise<RepairSessionStatus> {
    return createRepairSession(
      sourceStrictJobId,
      sessionId,
      roundId,
      jobId,
      accessToken,
      signal
    );
  }

  async repairSession(
    sessionId: string,
    accessToken: string,
    signal?: AbortSignal
  ): Promise<RepairSessionStatus> {
    return getRepairSession(sessionId, accessToken, signal);
  }

  async invalidateRepairSession(
    sessionId: string,
    accessToken: string,
    signal?: AbortSignal
  ): Promise<void> {
    await invalidateRepairSession(sessionId, accessToken, signal);
  }

  async createRepairRound(
    sessionId: string,
    accessToken: string,
    payload: {
      round_id: string;
      job_id: string;
      previous_round_id: string;
      action: "RETRY" | "REJECT_AND_RETRY";
      locked_repair_keys: string[];
    },
    signal?: AbortSignal
  ): Promise<RepairSessionStatus> {
    return createRepairRound(sessionId, accessToken, payload, signal);
  }

  async createPartialRepairRound(
    sessionId: string,
    accessToken: string,
    roundId: string,
    jobId: string,
    signal?: AbortSignal
  ): Promise<RepairSessionStatus> {
    return createPartialRepairRound(
      sessionId,
      accessToken,
      roundId,
      jobId,
      signal
    );
  }

  async repairCandidateTimetable<T>(
    sessionId: string,
    roundId: string,
    accessToken: string,
    viewSegment: "classes" | "teachers" | "rooms",
    objectId: string | null,
    weekParity: "ODD" | "EVEN",
    signal?: AbortSignal
  ): Promise<T> {
    return previewRepairCandidate<T>(
      sessionId,
      roundId,
      accessToken,
      viewSegment,
      objectId,
      weekParity,
      signal
    );
  }

  async acceptRepairCandidate<T>(
    projectId: string,
    sessionId: string,
    roundId: string,
    applicationId: string,
    accessToken: string,
    signal?: AbortSignal
  ): Promise<T> {
    const response = await acceptRepairCandidate<T>(
      sessionId,
      roundId,
      applicationId,
      accessToken,
      await this.requireDocument(projectId),
      signal
    );
    const result = await this.applySchedulingResponse(projectId, response);
    await acknowledgeRepairAcceptance(
      sessionId,
      applicationId,
      accessToken,
      signal
    );
    return result;
  }

  async export(
    projectId: string,
    payload: Record<string, unknown>,
    signal?: AbortSignal
  ): Promise<ExportDownload> {
    return exportDocument(await this.requireDocument(projectId), payload, signal);
  }

  async planningImportTemplate(
    projectId: string,
    signal?: AbortSignal
  ): Promise<ExportDownload> {
    return downloadPlanningImportTemplate(
      await this.requireDocument(projectId),
      signal
    );
  }

  async timetableVersions(projectId: string): Promise<TimetableVersionRecord[]> {
    await this.requireDocument(projectId);
    return this.versions.list(projectId);
  }

  async timetableVersion(
    projectId: string,
    versionId: string
  ): Promise<TimetableVersionRecord> {
    await this.requireDocument(projectId);
    const version = await this.versions.get(projectId, versionId);
    if (!version) {
      throw new ProjectRepositoryError("VERSION_NOT_FOUND", "课表版本不存在");
    }
    return version;
  }

  async timetableVersionHead(projectId: string) {
    await this.requireDocument(projectId);
    return this.versions.head(projectId);
  }

  async timetableForVersion<T>(
    projectId: string,
    versionId: string | null,
    view: "CLASS" | "TEACHER" | "ROOM",
    weekParity: "ODD" | "EVEN",
    signal?: AbortSignal
  ): Promise<T> {
    const current = await this.requireDocument(projectId);
    const head = await this.versions.head(projectId);
    let document = current;
    if (versionId && versionId !== head?.version_id) {
      const version = await this.versions.get(projectId, versionId);
      if (!version) {
        throw new ProjectRepositoryError("VERSION_NOT_FOUND", "课表版本不存在");
      }
      document = version.document_snapshot;
    }
    const response = await executeDocumentCommand<T>(
      document,
      "timetable.view",
      { view, object_id: null, week_parity: weekParity },
      signal
    );
    return response.result;
  }

  async exportTimetableVersion(
    projectId: string,
    versionId: string | null,
    payload: Record<string, unknown>,
    signal?: AbortSignal
  ): Promise<ExportDownload> {
    const current = await this.requireDocument(projectId);
    const head = await this.versions.head(projectId);
    let document = current;
    let versionContext: Record<string, unknown> | null = null;
    if (versionId) {
      const version = await this.versions.get(projectId, versionId);
      if (!version) {
        throw new ProjectRepositoryError("VERSION_NOT_FOUND", "课表版本不存在");
      }
      versionContext = versionContextFromRecord(version);
      if (versionId !== head?.version_id) document = version.document_snapshot;
    }
    return exportDocument(
      document,
      { ...payload, ...(versionContext ? { version_context: versionContext } : {}) },
      signal
    );
  }

  async isActiveTimetableVersion(
    projectId: string,
    versionId: string
  ): Promise<boolean> {
    await this.requireDocument(projectId);
    return (await this.versions.head(projectId))?.version_id === versionId;
  }

  close(): void {
    this.store.close();
    this.versions.close();
    this.prechecks.close();
  }

  private async requireDocument(projectId: string): Promise<ProjectDocumentV3> {
    let document = await this.store.get(projectId);
    if (!document) {
      throw new ProjectRepositoryError("PROJECT_NOT_FOUND", "课表项目不存在");
    }
    await this.versions.reconcileProject(this.store, projectId);
    document = await this.store.get(projectId);
    if (!document) {
      throw new ProjectRepositoryError("PROJECT_NOT_FOUND", "课表项目不存在");
    }
    await this.versions.ensureMigrated(document);
    return document;
  }

  private async requireUniqueName(rawName: string, excludedId?: string): Promise<void> {
    const nameKey = normalizeProjectName(rawName).toLocaleLowerCase("zh-CN");
    const existing = await this.store.list();
    if (
      existing.some(
        (document) =>
          document.id !== excludedId &&
          document.name.toLocaleLowerCase("zh-CN") === nameKey
      )
    ) {
      throw new ProjectRepositoryError("NAME_CONFLICT", "课表名称已存在");
    }
  }
}

let repositoryPromise: Promise<ProjectRepository> | null = null;

export function getProjectRepository(): Promise<ProjectRepository> {
  repositoryPromise ??= Promise.all([
    openProjectStore(),
    openTimetableVersionStore(),
    openSchedulingPrecheckStore()
  ]).then(
    ([store, versions, prechecks]) =>
      new ProjectRepository(store, versions, prechecks)
  );
  return repositoryPromise;
}

export function resetProjectRepositoryForTests(): void {
  if (repositoryPromise) {
    void repositoryPromise.then((repository) => repository.close());
  }
  repositoryPromise = null;
}

function versionApplication(value: unknown): VersionApplication | null {
  if (!isRecord(value) || !isRecord(value.version_application)) return null;
  const application = value.version_application;
  if (
    typeof application.application_id !== "string" ||
    !["STRICT", "REPAIR", "PARTIAL"].includes(String(application.source)) ||
    !["COMPLETE", "INCOMPLETE"].includes(String(application.completeness))
  ) {
    throw new ProjectRepositoryError(
      "DOCUMENT_INVALID",
      "排课服务返回了无效的版本应用信息"
    );
  }
  return {
    application_id: application.application_id,
    source: application.source as VersionApplication["source"],
    completeness: application.completeness as VersionApplication["completeness"],
    solver_status:
      typeof application.solver_status === "string"
        ? application.solver_status
        : null,
    session_id:
      typeof application.session_id === "string"
        ? application.session_id
        : undefined,
    round_id:
      typeof application.round_id === "string" ? application.round_id : undefined
  };
}

function versionProvenance(value: unknown) {
  if (!isRecord(value)) {
    return {
      actual_repairs: [],
      unassigned_requirements: [],
      blocker_factors: []
    };
  }
  const repairs = Array.isArray(value.repair_items)
    ? value.repair_items
    : Array.isArray(value.accepted_relaxations)
      ? value.accepted_relaxations
      : [];
  const unassigned = Array.isArray(value.unassigned) ? value.unassigned : [];
  return {
    actual_repairs: structuredClone(repairs),
    unassigned_requirements: structuredClone(unassigned),
    blocker_factors: unassigned.flatMap((row) =>
      isRecord(row) && Array.isArray(row.blockers) ? row.blockers : []
    )
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function versionContextFromRecord(version: TimetableVersionRecord) {
  return {
    version_id: version.id,
    label: version.label,
    source: version.source,
    completeness: version.completeness,
    scheduled_lessons: version.scheduled_lessons,
    required_lessons: version.required_lessons,
    solver_status: version.solver_status,
    actual_repairs: version.actual_repairs,
    unassigned_requirements: version.unassigned_requirements,
    blocker_factors: version.blocker_factors
  };
}
